/**
 * CookieConsentBanner — first-visit cookie notice.
 *
 * Shows once per browser until the user picks Accept or Decline. Choice
 * is stored in localStorage under `cookieConsent` (matches the cookie
 * policy doc). Mounted at the App root so it appears on every page.
 *
 * Visual style matches the light home theme: white card with a gradient
 * accent bar, dark primary pill, ghost secondary, theme fonts (Hanken +
 * Inter), centered on every breakpoint. Global (mounted at App root).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  CONSENT_STORAGE_KEY,
  type ConsentChoice,
  replayStoredConsent,
  setStoredConsent,
} from '../lib/consent';

const VISIBLE_DELAY_MS = 1200; // small delay so it doesn't fight the loader

export const CookieConsentBanner = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Replay prior consent into gtag (and Clarity, if configured) so
    // returning visitors don't lose tracking just because the banner
    // already dismissed.
    replayStoredConsent();
    if (window.localStorage.getItem(CONSENT_STORAGE_KEY)) return; // already chose
    const t = window.setTimeout(() => setOpen(true), VISIBLE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  const choose = (choice: ConsentChoice) => {
    // Persists + applies to every consent-aware vendor (GA4, Clarity).
    // Decline keeps analytics in 'denied' so no _ga / _gid cookies get
    // set and no measurement hits fire.
    setStoredConsent(choice);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          // Centered overlay — banner sits in the middle of the
          // viewport on every breakpoint so it can't be missed. The
          // surrounding dimmed scrim is purely visual (pointer-events
          // stay on the panel itself so the page underneath remains
          // interactive — banner is dismissed by a button, not a tap-
          // outside).
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          role="dialog"
          aria-label="Cookie consent"
          aria-live="polite"
          aria-modal="true"
        >
          <div
            className="pointer-events-auto relative overflow-hidden bg-white border border-[#e8e7e4] rounded-2xl p-5 md:p-6 w-full max-w-[460px]"
            style={{
              fontFamily: 'Inter, -apple-system, "Segoe UI", Roboto, sans-serif',
              boxShadow: '0 26px 60px -16px rgba(65,53,80,0.35), 0 2px 8px rgba(65,53,80,0.10)',
            }}
          >
            {/* Theme gradient accent bar */}
            <span
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-[3px]"
              style={{ background: 'linear-gradient(90deg,#ff9e6b,#f5749f,#9a7bf0,#5f7bf0)' }}
            />
            <p className="font-bold tracking-[0.22em] text-[#9a9b9e] text-[10px] uppercase mb-2">
              Cookies
            </p>
            <p
              className="text-[#171718] text-xl md:text-2xl leading-tight mb-3"
              style={{ fontFamily: '"Hanken Grotesk", system-ui, sans-serif', fontWeight: 700 }}
            >
              We use a few cookies.
            </p>
            <p className="text-[#57585b] text-sm leading-relaxed mb-5">
              Essential cookies keep the site working; optional analytics
              help us improve it. You can change your mind anytime.{' '}
              <Link
                to="/cookies"
                className="text-[#171718] font-medium underline underline-offset-4 hover:no-underline"
              >
                Learn more
              </Link>
              .
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => choose('declined')}
                className="font-bold text-[11px] uppercase tracking-[0.22em] text-[#57585b] hover:text-[#111] border border-[#dedede] hover:border-[#111] rounded-full px-5 py-3 transition-colors"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={() => choose('accepted')}
                className="font-bold text-[11px] uppercase tracking-[0.22em] text-white bg-[#111] hover:bg-black rounded-full px-5 py-3 transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
